import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { SIGNABLE_VOID_REASON_MAX_LENGTH } from '../../../enums/Constraints';
import { SignableSchema } from '../../../definitions/schemas';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import SignablesInterface from '../../../graphql/Signables/SignablesInterface';
import SnackbarStore from '../../../stores/observables/SnackbarStore';
import { trackSignableEvent } from '../analytics';
import { Events, Properties } from '../../../enums/Analytics';

interface Props {
  onClose: () => void;
  onVoided: (signable: SignableSchema) => void;
  open: boolean;
  referrer: string;
  signable: SignableSchema;
}

const VoidDialog: React.FC<Props> = (props: Props): JSX.Element => {
  const { onClose, onVoided, open, referrer, signable } = props;

  const [notifySigners, setNotifySigners] = useState(false);
  const [reason, setReason] = useState('');

  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { current: validator } = useRef(new FormValidatorHelper());

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSaving(false);
        setSubmitted(false);
        setNotifySigners(false);
        setReason('');
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitted(true);

    if (validator.isValid()) {
      onVoid();
    }
  };

  const onVoid = async () => {
    try {
      setSaving(true);

      const {
        voidSignable: { signable: updatedSignable },
      } = await new SignablesInterface().void({
        id: signable.id,
        reason,
        notifySigners,
      });

      trackSignableEvent(
        Events.SIGNABLE_DOCUMENT_VOIDED,
        referrer,
        updatedSignable,
        {
          [Properties.REASON]: reason,
        }
      );

      onVoided(updatedSignable);
      onClose();
    } catch (e) {
      SnackbarStore.show(`${e}`);
      setSaving(false);
    }
  };

  const onNotifySignersChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setNotifySigners(checked);
    setSubmitted(false);

    if (!checked) {
      setReason('');
    }
  };

  const onReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const max = `max:${SIGNABLE_VOID_REASON_MAX_LENGTH}`;
  validator.check('reason', reason, !notifySigners ? max : `required|${max}`);

  const reasonError = validator.getError('reason', submitted);

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle>Void document</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <DialogContentText>
            All signing activities will be cancelled
          </DialogContentText>

          <Box mt={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifySigners}
                  disabled={saving}
                  color="secondary"
                  onChange={onNotifySignersChange}
                  size="small"
                />
              }
              label="Notify assigned signers via email"
            />
          </Box>
          <Fade in={notifySigners} mountOnEnter unmountOnExit>
            <Box ml={3.5} my={1.5}>
              <TextField
                autoFocus
                color="secondary"
                disabled={saving}
                error={!!reasonError}
                fullWidth
                helperText={reasonError}
                inputProps={{
                  maxLength: SIGNABLE_VOID_REASON_MAX_LENGTH,
                }}
                label="Reason for voiding"
                onChange={onReasonChange}
                size="small"
                required
                value={reason}
              />
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={saving}
            type="submit"
            variant="contained"
          >
            Void
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VoidDialog;
